import React from "react";
import { Box, Switch, Typography, TextField, Stack, Chip, Tooltip } from "@mui/material";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import {
  MANAGER_COLORS,
  dragListContainerSx,
  featureRowSx,
  helperCaptionSx,
} from "./managerTheme.js";

const FEATURE_LABELS = {
  caseSheet: "Case Sheet",
  patient360: "Patient 360",
  voicePrescription: "Voice Prescription",
  transcription: "Transcription",
  autosaveDraft: "Autosave Draft",
  telemedicine: "Telemedicine",
};

const AUTOSAVE_MIN_INTERVAL_MS = 15000;

const FEATURE_DESCRIPTIONS = {
  caseSheet: "Show the case sheet panel in the doctor console.",
  patient360: "Enable Patient 360 integration in the console.",
  voicePrescription: "Allow voice-driven prescription entry.",
  transcription: "Enable live transcription during consultation.",
  autosaveDraft: "Automatically save consultation drafts at a set interval.",
  telemedicine:
    "Show the Telemedicine button and video-call panel in the doctor console.",
};

const FeaturesEditor = ({ features, onChange, telemedicineEnabled = false }) => {
  const updateFeature = (id, patch) => {
    if (id === "telemedicine" && patch.enabled === true && !telemedicineEnabled) {
      return;
    }
    onChange((prev) => ({
      ...prev,
      [id]: { ...prev[id], ...patch },
    }));
  };

  const entries = Object.entries(features ?? {});

  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
        <TuneOutlinedIcon sx={{ fontSize: 18, color: MANAGER_COLORS.primary }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: MANAGER_COLORS.primaryDark }}>
          Console features
        </Typography>
        <Chip
          size="small"
          label={`${entries.filter(([, f]) => f.enabled !== false).length} enabled`}
          sx={{ height: 22, fontSize: "0.7rem", bgcolor: MANAGER_COLORS.accentLight }}
        />
      </Stack>
      <Typography sx={helperCaptionSx}>
        Toggle optional capabilities for this doctor&apos;s console experience.
      </Typography>

      <Box sx={dragListContainerSx}>
        {entries.map(([id, feature]) => {
          const isOn = feature.enabled !== false;
          const telemedicineLockedOff =
            id === "telemedicine" && !telemedicineEnabled && !isOn;
          const description =
            id === "telemedicine" && !telemedicineEnabled
              ? "Telemedicine can be turned on only when it is enabled for this facility."
              : (FEATURE_DESCRIPTIONS[id] ?? "Feature toggle");

          return (
            <Box key={id} sx={featureRowSx}>
              <Switch
                checked={isOn}
                disabled={telemedicineLockedOff}
                onChange={(e) => updateFeature(id, { enabled: e.target.checked })}
              />
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: MANAGER_COLORS.primaryDark }}>
                  {FEATURE_LABELS[id] ?? id}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {description}
                </Typography>
              </Box>

              {feature.visible && (
                <Tooltip
                  title={`Also gated by a runtime rule: ${feature.visible.when} ${feature.visible.path ?? ""} ${
                    feature.visible.value ?? ""
                  }`}
                >
                  <Chip
                    size="small"
                    label="Conditional rule"
                    variant="outlined"
                    sx={{ borderColor: MANAGER_COLORS.border, fontSize: "0.7rem" }}
                  />
                </Tooltip>
              )}

              {id === "autosaveDraft" && feature.enabled !== false && (
                <TextField
                  size="small"
                  type="number"
                  label="Interval (ms)"
                  value={feature.intervalMs ?? 30000}
                  onChange={(e) => updateFeature(id, { intervalMs: Number(e.target.value) })}
                  onBlur={(e) => {
                    const clamped = Math.max(
                      AUTOSAVE_MIN_INTERVAL_MS,
                      Number(e.target.value) || 0,
                    );
                    if (clamped !== Number(e.target.value)) {
                      updateFeature(id, { intervalMs: clamped });
                    }
                  }}
                  inputProps={{ min: AUTOSAVE_MIN_INTERVAL_MS }}
                  helperText={`Min ${AUTOSAVE_MIN_INTERVAL_MS}ms`}
                  sx={{
                    width: 160,
                    "& .MuiOutlinedInput-root": { bgcolor: "#fafcfd" },
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default FeaturesEditor;
